import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  CheckCircle,
  Download,
  Eye,
  Mail,
  Pencil,
  Plus,
  Send,
  Trash2,
  User,
  Users,
} from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { STAFF_MEMBERS_CONTROLER_URL } from "api/Service";
import emptyDocs from "assets/images/empty-docs.svg";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { useDocumentTypes } from "features/documents/hooks/useDocumentTypes";
import { canMarkDocumentReviewed } from "features/documents/utils/documentReview";
import {
  formatDocumentTypeLabel,
  getDocumentTypeIcon,
} from "utils/documentTypeIcons";
import { pathGenerators } from "utils/routes";

import {
  formatDocumentDate,
  formatStatusLabel,
} from "../../../../pages/patient/PatientDocumentsPage/utils/documentFormatters";
import styles from "./DocumentsTable.module.css";

const FILTER_GROUPS = [
  {
    id: "status",
    label: "Status",
    type: "multi",
    options: [
      { value: "NEW", label: "New" },
      { value: "IN_REVIEW", label: "In Review" },
      { value: "REVIEWED", label: "Reviewed" },
      { value: "UPLOADED", label: "Uploaded" },
      { value: "SYSTEM_GENERATED", label: "System Generated" },
    ],
  },
];

const getFileType = (doc) => {
  if (doc.documentUrl) {
    const urlWithoutParams = doc.documentUrl.split("?")[0];
    const ext = urlWithoutParams.split(".").pop()?.toUpperCase();
    if (ext && ext.length <= 5) return ext;
  }
  if (doc.fileName) {
    const ext = doc.fileName.split(".").pop()?.toUpperCase();
    if (ext && ext.length <= 5) return ext;
  }
  const mimeMap = {
    "application/pdf": "PDF",
    "image/jpeg": "JPG",
    "image/png": "PNG",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "DOCX",
  };
  return doc.mimeType ? mimeMap[doc.mimeType] || "-" : "-";
};

const getStatusPillColors = (status) => {
  if (!status) return PILL_COLORS.docUnknown;
  const map = {
    NEW: PILL_COLORS.docNew,
    IN_REVIEW: PILL_COLORS.docInProgress,
    REVIEWED: PILL_COLORS.docReviewed,
    CLOSED: PILL_COLORS.docClosed,
    UPLOADED: PILL_COLORS.docUploaded,
    SYSTEM_GENERATED: PILL_COLORS.docSystemGenerated,
    REFERRAL: PILL_COLORS.docReferral,
  };
  return map[status.toUpperCase()] || PILL_COLORS.docUnknown;
};

export const DocumentsTable = ({
  documents = [],
  isLoading = false,
  filters,
  selection,
  onUploadClick,
  onView,
  onDelete,
  onDownload,
  onEdit,
  onChangeStatus,
  onReview,
  onSendEmail,
  onPrint,
  onBulkChangeStatus,
  onBulkSendEmail,
  onBulkDownload,
  documentType = "",
  ownershipFilter = "ALL",
  onOwnershipFilterChange,
  currentPage = 1,
  rowsPerPage = 20,
  rowsOptions,
  rowsValue,
  onRowsChange,
  onPageChange,
  serverTotalCount,
}) => {
  const { data: allDocumentTypes = [] } = useDocumentTypes();
  const activeDocType = allDocumentTypes.find((dt) => dt.name === documentType);
  const [staffMembers, setStaffMembers] = useState([]);
  const navigate = useNavigate();

  const tenant =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const fetchStaffMembers = useCallback(async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenant}${STAFF_MEMBERS_CONTROLER_URL}`
      );
      setStaffMembers(response.data || []);
    } catch (error) {
      console.error("Error fetching staff members:", error);
    }
  }, [tenant]);

  useEffect(() => {
    fetchStaffMembers();
  }, [fetchStaffMembers]);

  const formatDocType = (type) => {
    if (!type) return "";
    if (activeDocType?.label) return activeDocType.label;
    return formatDocumentTypeLabel(type);
  };

  const getDocTypeIcon = (type) => {
    if (!type) return null;
    const Icon = getDocumentTypeIcon(type, activeDocType?.icon);
    return <Icon size={20} />;
  };

  const handlePatientClick = useCallback(
    (doc) => {
      const patientId = doc.patient?.id;
      if (!patientId) return;
      navigate(pathGenerators.patient.documents(patientId));
    },
    [navigate]
  );

  const getReviewerName = (doc) => {
    const formatStaffDisplay = (staff) => {
      if (!staff) return null;
      const name = `${staff.firstName || ""} ${staff.lastName || ""}`.trim();
      if (!name) return null;
      const activePrefix = staff.active ? "✅ " : "";
      const roleText = staff.staffRole
        ? ` (${staff.staffRole.toLowerCase()})`
        : "";
      return `${activePrefix}${name}${roleText}`;
    };

    if (doc.reviewedById && staffMembers.length > 0) {
      const staff = staffMembers.find((s) => s.id === doc.reviewedById);
      const display = formatStaffDisplay(staff);
      if (display) return display;
    }
    if (doc.reviewedBy?.firstName || doc.reviewedBy?.lastName) {
      const display = formatStaffDisplay(doc.reviewedBy);
      if (display) return display;
    }
    if (doc.reviewerStaff?.firstName || doc.reviewerStaff?.lastName) {
      const display = formatStaffDisplay(doc.reviewerStaff);
      if (display) return display;
    }
    return "-";
  };

  const hasActiveFilters =
    filters.searchQuery?.length > 0 ||
    filters.statusFilter?.length > 0 ||
    filters.priorityFilter?.length > 0;

  const selectionAdapter = useMemo(
    () => ({
      selectedIds: new Set(selection.selectedIds || []),
      selectedCount: selection.selectedCount ?? 0,
      isSelected: selection.isSelected,
      toggleSelection: (id, row, checked) => {
        if (checked !== undefined) {
          const currentlySelected = selection.isSelected(id);
          if (checked && !currentlySelected) selection.toggleSelection(id, row);
          else if (!checked && currentlySelected)
            selection.toggleSelection(id, row);
        } else {
          selection.toggleSelection(id, row);
        }
      },
      selectAll: selection.selectAll,
      deselectAll: selection.deselectAll,
    }),
    [selection]
  );

  const showDocumentType = !documentType || documentType === "ALL";

  const columns = useMemo(
    () => {
      const cols = [
        {
          id: "patient",
          header: "Patient",
          width: "230px",
          render: (doc) => {
            const patientName = doc.patient
              ? `${doc.patient.firstName || ""} ${
                  doc.patient.surname || ""
                }`.trim()
              : "N/A";
            return patientName;
          },
          getTooltipText: (doc) => {
            const patientName = doc.patient
              ? `${doc.patient.firstName || ""} ${
                  doc.patient.surname || ""
                }`.trim()
              : "N/A";
            return patientName;
          },
          onClick: (doc) => doc.patient?.id && handlePatientClick(doc),
        },
        {
          id: "documentName",
          header: "Document Name",
          width: "300px",
          render: (doc) => doc.name || doc.fileName || "Untitled",
          getTooltipText: (doc) => doc.name || doc.fileName || "Untitled",
        },
      ];

      if (showDocumentType) {
        cols.push({
          id: "documentType",
          header: "Document Type",
          width: "170px",
          render: (doc) =>
            doc.documentType ? formatDocumentTypeLabel(doc.documentType) : "-",
        });
      }

      cols.push(
        {
          id: "fileType",
          header: "File Type",
          width: "120px",
          render: (doc) => (
            <Pill {...PILL_COLORS.method}>{getFileType(doc)}</Pill>
          ),
        },
        {
          id: "reviewer",
          header: "Reviewer",
          width: "190px",
          render: (doc) => getReviewerName(doc),
          getTooltipText: (doc) => getReviewerName(doc),
        },
        {
          id: "dateUploaded",
          header: "Date Uploaded",
          width: "170px",
          render: (doc) =>
            formatDocumentDate(
              doc.uploadedDate ||
                doc.dateUploaded ||
                doc.created ||
                doc.createdDate
            ),
        },
        {
          id: "status",
          header: "Status",
          width: "minmax(min-content, 1fr)",
          render: (doc) => (
            <Pill {...getStatusPillColors(doc.status)}>
              {formatStatusLabel(doc.status)}
            </Pill>
          ),
        }
      );

      return cols;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staffMembers, handlePatientClick, showDocumentType]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Open preview",
        icon: Eye,
        onClick: (doc) => onView?.(doc),
      },
      {
        label: "Change status",
        icon: Pencil,
        onClick: (doc) => onChangeStatus?.(doc),
      },
      {
        label: "Download",
        icon: Download,
        onClick: (doc) => onDownload?.(doc),
        primary: true,
      },
      {
        label: "Print",
        icon: Send,
        onClick: (doc) => onPrint?.(doc),
      },
      {
        label: "Send Email",
        icon: Mail,
        onClick: (doc) => onSendEmail?.(doc),
      },
      {
        label: "Mark as reviewed",
        icon: CheckCircle,
        visibleWhen: (doc) => canMarkDocumentReviewed(doc.status),
        onClick: (doc) => onReview?.(doc),
      },
      {
        label: "Edit",
        icon: Pencil,
        onClick: (doc) => onEdit?.(doc),
      },
      {
        label: "Delete",
        icon: Trash2,
        onClick: (doc) => onDelete?.(doc),
      },
    ],
    [
      onView,
      onDownload,
      onEdit,
      onChangeStatus,
      onReview,
      onSendEmail,
      onPrint,
      onDelete,
    ]
  );

  const bulkActions = useMemo(
    () => [
      { label: "Download", icon: Download, onClick: onBulkDownload },
      { label: "Change status", icon: Pencil, onClick: onBulkChangeStatus },
      { label: "Send email", icon: Mail, onClick: onBulkSendEmail },
    ],
    [onBulkDownload, onBulkChangeStatus, onBulkSendEmail]
  );

  return (
    <div className={styles.documentList}>
      <div className={styles.header}>
        <p className={styles.title}>
          {getDocTypeIcon(documentType)}
          {formatDocType(documentType) || "Documents"}
        </p>
        <div className={styles.headerActions}>
          <Button variant="text" onClick={onUploadClick} endIcon={Plus}>
            Add new document
          </Button>
          <div className={styles.ownershipSwitcher}>
            <TabSwitcher
              options={[
                { value: "MINE", label: "For me", icon: <User size={16} /> },
                { value: "ALL", label: "All", icon: <Users size={16} /> },
              ]}
              value={ownershipFilter}
              onChange={onOwnershipFilterChange}
              size="small"
            />
          </div>
        </div>
      </div>

      <div className={`${styles.tableWrapper} ${styles.tableWrapperDataTable}`}>
        <DataTable
          columns={columns}
          data={documents}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          emptyIllustration={hasActiveFilters ? undefined : emptyDocs}
          emptyTitle="No documents found"
          emptyText={
            hasActiveFilters
              ? "Try adjusting your search or filters to find what you're looking for."
              : "Once the patient receives a document, it will appear here."
          }
          search={{
            value: filters.searchQuery,
            onChange: filters.setSearch,
            placeholder: "Search by patient",
          }}
          sort={{
            field: filters.sortField,
            direction: filters.sortDirection,
            onChange: filters.onSortChange,
          }}
          sortOptions={[
            {
              field: "created",
              direction: "desc",
              label: "Date (Newest first)",
              icon: ArrowDownNarrowWide,
            },
            {
              field: "created",
              direction: "asc",
              label: "Date (Oldest first)",
              icon: ArrowUpNarrowWide,
            },
          ]}
          filters={{
            value: { status: filters.statusFilter },
            onChange: (groupId, value) => {
              if (groupId === "status") filters.onStatusFilterChange(value);
            },
          }}
          filtersConfig={FILTER_GROUPS}
          selectable
          selection={selectionAdapter}
          topActions={[]}
          rowActions={rowActions}
          bulkActions={bulkActions}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalCount={serverTotalCount}
          rowsOptions={rowsOptions}
          rowsValue={rowsValue}
          onRowsChange={onRowsChange}
          onPageChange={(page) => onPageChange?.({}, page)}
        />
      </div>
    </div>
  );
};
